import { useState } from "react";
import { useThemeContext } from "../../../../context/ThemeContext";
import NavigationButtons from "../Shared/NavigationButtons";

const Step6ReviewSubmit = ({ assessmentSetup, onBack, onSubmit }) => {
  const { darkMode } = useThemeContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit();
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`p-6 rounded-xl mb-4 shadow-lg transition-all duration-300 ${
        darkMode
          ? "bg-gradient-to-br from-[#0D1117] to-[#151B26] border border-[#252525]"
          : "bg-gradient-to-br from-white to-gray-50 border border-[#E0E0E0]"
      }`}
    >
      <div className="flex items-center mb-4">
        <div
          className={`p-2 rounded-lg mr-3 ${darkMode ? "bg-blue-900/30" : "bg-blue-100"}`}
        >
          <svg
            className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-blue-600"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h3
          className={`text-xl font-bold ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          Review & Submit
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div
          className={`p-4 rounded-xl transition-colors duration-300 ${
            darkMode
              ? "bg-[#161B22] border border-[#30363D]"
              : "bg-gray-50 border border-gray-200"
          }`}
        >
          <h4
            className={`font-semibold mb-2 flex items-center text-sm ${
              darkMode ? "text-white" : "text-gray-800"
            }`}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
              />
            </svg>
            Assessment Structure
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <p
                className={`text-xs font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Components
              </p>
              <p
                className={`text-xs font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Count
              </p>
            </div>
            <div className="flex justify-between">
              <p
                className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Assignments
              </p>
              <p
                className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {assessmentSetup.assignments}
              </p>
            </div>
            <div className="flex justify-between">
              <p
                className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Class Tests
              </p>
              <p
                className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {assessmentSetup.classTests}
              </p>
            </div>
            <div className="flex justify-between">
              <p
                className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Projects
              </p>
              <p
                className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {assessmentSetup.projects}
              </p>
            </div>
          </div>
        </div>

        <div
          className={`p-4 rounded-xl transition-colors duration-300 ${
            darkMode
              ? "bg-[#161B22] border border-[#30363D]"
              : "bg-gray-50 border border-gray-200"
          }`}
        >
          <h4
            className={`font-semibold mb-2 flex items-center text-sm ${
              darkMode ? "text-white" : "text-gray-800"
            }`}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            PRS Status
          </h4>
          <div className="flex items-center justify-between mt-3">
            <p
              className={`text-xs font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Status
            </p>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                assessmentSetup.includePRS
                  ? darkMode
                    ? "bg-green-900/30 text-green-400"
                    : "bg-green-100 text-green-800"
                  : darkMode
                    ? "bg-gray-800 text-gray-400"
                    : "bg-gray-100 text-gray-800"
              }`}
            >
              {assessmentSetup.includePRS ? "Included" : "Not Included"}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-5">
        <h4
          className={`font-semibold mb-2 flex items-center text-sm ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          Important Notes:
        </h4>
        <ul
          className={`space-y-2 text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          <li className="flex items-start">
            <svg
              className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Verify all data before submission</span>
          </li>

          <li className="flex items-start">
            <svg
              className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Ensure weightages sum to 100% across all assessments</span>
          </li>
        </ul>
      </div>

      <NavigationButtons
        onBack={onBack}
        onNext={handleSubmit}
        nextLabel={
          isSubmitting ? (
            <span className="flex items-center text-sm">
              <svg
                className="animate-spin -ml-1 mr-2 h-3 w-3 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Submitting...
            </span>
          ) : (
            "Submit CO-PO Attainment"
          )
        }
        nextDisabled={isSubmitting}
        darkMode={darkMode}
      />
    </div>
  );
};

export default Step6ReviewSubmit;
